import { isFunctionType } from 'chairisher/util/type';

/**
 * Represents an error that occurs during the loading or execution of an external JavaScript script.
 */
class ScriptError extends Error {
    constructor({ scriptName, scriptSrc, type }) {
        super();
        this.message = `"${scriptSrc}": ${this.generateMessage(scriptName, scriptSrc, type)}`;
        this.name = 'ScriptError';
        this.scriptName = scriptName;
        this.scriptSrc = scriptSrc;
        this.type = type;
    }

    generateMessage(scriptName, scriptSrc, type) {
        switch (type) {
            case 'error':
                return 'Network or loading issue (see: connectivity problems, ad blockers, CSP blocking)';
            case 'load':
                return `Script loaded, but ${scriptName} object not found (see: browser extensions, execution failure)`;
            case 'missing_script_data':
                return `Missing required script data. scriptName: ${scriptName}, scriptSrc: ${scriptSrc}`;
            default:
                return 'Unknown script error. Check network and script configuration.';
        }
    }
}

/**
 * Dynamically loads a JavaScript script and appends it to the document head.
 *
 * @param {Object} settings
 * @param {Any=} settings.globalArg Optional arg to pass into the global property, if property is present and functional.
 * @param {Function=} settings.rejectCallback Optional callback function to execute when script fails to load.
 * @param {String} settings.scriptName The name of the script; must match global property.
 * @param {String} settings.scriptSrc The URL of the script to load.
 * @param {Boolean=} settings.shouldImmediatelyResolve Optional indicater of whether the Promise should resolve immediately with the native Event object.
 * @return {Promise<Object>} Promise that resolves when script is loaded and expected global property is available, or rejects on error.
 * @throws {ScriptError}
 */
export default function loadScript({
    globalArg = null,
    rejectCallback = () => {},
    scriptName,
    scriptSrc,
    shouldImmediatelyResolve = false,
}) {
    return new Promise((resolve, reject) => {
        if (!(scriptName && scriptSrc)) {
            reject(new ScriptError({ scriptName, scriptSrc, type: 'missing_script_data' }));
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = scriptSrc;

        const rejectWrapper = ({ type }) => {
            rejectCallback();
            reject(new ScriptError({ scriptName, scriptSrc, type }));
        };

        script.onerror = rejectWrapper;

        script.onload = shouldImmediatelyResolve
            ? resolve
            : ({ type }) => {
                  const globalProperty = window[scriptName];
                  if (globalProperty) {
                      resolve(isFunctionType(globalProperty) ? globalProperty(globalArg) : globalProperty);
                  } else {
                      rejectWrapper({ type });
                  }
              };

        document.head.appendChild(script);
    });
}
